import {
  DotGraph,
  DotStatement,
  GraphLayout,
  LayoutNode,
  LayoutEdge,
  LayoutCluster,
  NodeAttributes,
  EdgeAttributes,
  NodeShape,
  Edge,
  Subgraph,
  GraphAttributes,
  parseLabel,
  LabelContent,
} from './types';
import { networkSimplexRankAssignment } from './rank';

function attrsToRecord(attrs: GraphAttributes): Record<string, string> {
  return {
    rankdir: attrs.rankdir || 'TB',
    ranksep: String(attrs.ranksep || 50),
    nodesep: String(attrs.nodesep || 18),
    margin: String(attrs.margin || 8),
  };
}

interface Adjacency {
  nodes: string[];
  preds: Map<string, string[]>;
  succs: Map<string, string[]>;
}

function makeAcyclic(adj: Adjacency): Set<string> {
  const color = new Map<string, number>(); // 0 white, 1 gray, 2 black
  const finish = new Map<string, number>();
  let time = 0;

  function dfs(u: string) {
    color.set(u, 1);
    const succs = adj.succs.get(u) || [];
    for (const v of succs) {
      if (color.get(v) === 0) {
        dfs(v);
      }
    }
    color.set(u, 2);
    finish.set(u, time++);
  }

  for (const node of adj.nodes) {
    color.set(node, 0);
  }

  for (const node of adj.nodes) {
    if (color.get(node) === 0) {
      dfs(node);
    }
  }

  const reversed = new Set<string>();
  for (const u of adj.nodes) {
    const succs = adj.succs.get(u) || [];
    for (const v of succs.slice()) {
      if (finish.get(v)! > finish.get(u)!) {
        // v finishes later -> back edge
        adj.succs.set(
          u,
          succs.filter((x) => x !== v)
        );
        adj.preds.set(
          v,
          (adj.preds.get(v) || []).filter((x) => x !== u)
        );
        adj.succs.set(v, (adj.succs.get(v) || []).concat(u));
        adj.preds.set(u, (adj.preds.get(u) || []).concat(v));
        reversed.add(`${u}|${v}`);
      }
    }
  }

  return reversed;
}

export function layoutGraph(graph: DotGraph): GraphLayout {
  const nodes = collectNodes(graph);
  const edges = collectEdges(graph);

  // Collect rank constraints
  const sameRankGroups: string[][] = [];
  function collectNodeIdsFromSubgraph(subgraph: Subgraph): string[] {
    const nodeIds: string[] = [];
    for (const stmt of subgraph.statements) {
      if (stmt.type === 'node') {
        nodeIds.push(stmt.nodeId);
      } else if (stmt.type === 'subgraph') {
        nodeIds.push(...collectNodeIdsFromSubgraph(stmt));
      }
    }
    return nodeIds;
  }

  for (const stmt of graph.statements) {
    if (stmt.type === 'subgraph' && stmt.attributes.rank === 'same') {
      const nodeIds = collectNodeIdsFromSubgraph(stmt);
      if (nodeIds.length > 0) {
        sameRankGroups.push(nodeIds);
      }
    }
  }

  // Collect node-level rank constraints
  const nodeRankConstraints = new Map<string, 'min' | 'max' | 'source' | 'sink'>();
  for (const [id, attrs] of nodes) {
    if (attrs.rank && attrs.rank !== 'same') {
      nodeRankConstraints.set(id, attrs.rank);
    }
  }

  const filteredEdges = edges.filter((edge) => {
    const {constraint} = edge.attributes;
    return constraint !== 'false';
  });

  const adj = buildAdjacency(nodes, filteredEdges);
  const reversedEdges = makeAcyclic(adj);

  // Build acyclic edges list reflecting reversals
  const edgeMap = new Map<string, Edge>();
  for (const edge of filteredEdges) {
    edgeMap.set(`${edge.from}|${edge.to}`, edge);
  }
  const acyclicEdges: Edge[] = [];
  for (const u of adj.nodes) {
    for (const v of adj.succs.get(u) || []) {
      const key = `${u}|${v}`;
      const revKey = `${v}|${u}`;
      let edge = edgeMap.get(key);
      if (!edge && reversedEdges.has(revKey)) {
        // Edge was reversed, flip direction
        edge = edgeMap.get(revKey);
        if (edge) {
          acyclicEdges.push({ from: u, to: v, attributes: edge.attributes });
          continue;
        }
      }
      if (edge) {
        acyclicEdges.push(edge);
      }
    }
  }

  const graphAttrs = attrsToRecord(graph.attributes);
  const ranksep = parseFloat(graphAttrs.ranksep || '50');
  const ranks = networkSimplexRankAssignment(
    adj.nodes,
    acyclicEdges,
    adj.preds,
    adj.succs,
    ranksep
  );

  // Adjust ranks for node-level rank constraints
  if (nodeRankConstraints.size > 0) {
    const allRanks = Array.from(ranks.values());
    const minRank = Math.min(...allRanks);
    const maxRank = Math.max(...allRanks);
    for (const [nodeId, constraint] of nodeRankConstraints) {
      if (constraint === 'min' || constraint === 'source') {
        ranks.set(nodeId, minRank);
      } else if (constraint === 'max' || constraint === 'sink') {
        ranks.set(nodeId, maxRank);
      }
    }
  }

  // Apply rank=same constraints
  for (const group of sameRankGroups) {
    let total = 0;
    for (const nodeId of group) {
      total += ranks.get(nodeId) || 0;
    }
    const avg = total / group.length;
    for (const nodeId of group) {
      ranks.set(nodeId, avg);
    }
  }
  const rankGroups = orderNodes(ranks, filteredEdges);
  const layoutNodes = computeCoordinates(rankGroups, nodes, graphAttrs);
  const clusters = layoutClusters(graph, layoutNodes);
  const layoutEdges = routeEdges(layoutNodes, edges, graphAttrs);
  const boundingBox = computeBoundingBox(layoutNodes, layoutEdges);

  return {
    nodes: layoutNodes,
    edges: layoutEdges,
    clusters,
    boundingBox,
    rankDirection: graph.attributes.rankdir || 'TB',
  };
}

function collectNodes(graph: DotGraph): Map<string, NodeAttributes> {
  const nodes = new Map<string, NodeAttributes>();
  let globalNodeAttrs: NodeAttributes = {};

  function processStatements(statements: DotStatement[]) {
    for (const stmt of statements) {
      if (stmt.type === 'node') {
        nodes.set(stmt.nodeId, { ...globalNodeAttrs, ...stmt.attributes });
      } else if (stmt.type === 'edge') {
        for (const edge of stmt.edges) {
          if (!nodes.has(edge.from)) nodes.set(edge.from, { ...globalNodeAttrs });
          if (!nodes.has(edge.to)) nodes.set(edge.to, { ...globalNodeAttrs });
        }
      } else if (stmt.type === 'attr' && stmt.target === 'node') {
        globalNodeAttrs = { ...globalNodeAttrs, ...stmt.attributes };
      } else if (stmt.type === 'subgraph') {
        processStatements(stmt.statements);
      }
    }
  }

  processStatements(graph.statements);
  return nodes;
}

function collectEdges(graph: DotGraph): Edge[] {
  const edges: Edge[] = [];

  function processStatements(statements: DotStatement[]) {
    for (const stmt of statements) {
      if (stmt.type === 'edge') {
        edges.push(...stmt.edges);
      } else if (stmt.type === 'subgraph') {
        processStatements(stmt.statements);
      }
    }
  }

  processStatements(graph.statements);
  return edges;
}

function buildAdjacency(nodes: Map<string, NodeAttributes>, edges: Edge[]): Adjacency {
  const preds = new Map<string, string[]>();
  const succs = new Map<string, string[]>();

  for (const [id] of nodes) {
    preds.set(id, []);
    succs.set(id, []);
  }

  for (const edge of edges) {
    succs.get(edge.from)?.push(edge.to);
    preds.get(edge.to)?.push(edge.from);
  }

  return { nodes: Array.from(nodes.keys()), preds, succs };
}

function assignRanks(adj: Adjacency, attrs: Record<string, string>): Map<string, number> {
  const ranks = new Map<string, number>();
  const ranksep = parseFloat(attrs.ranksep || '50');

  const sources: string[] = [];
  for (const node of adj.nodes) {
    if ((adj.preds.get(node) || []).length === 0) {
      sources.push(node);
    }
  }

  if (sources.length > 0) {
    const visited = new Set<string>();
    const queue: [string, number][] = sources.map((s) => [s, 0]);

    while (queue.length > 0) {
      const [node, dist] = queue.shift()!;
      if (visited.has(node)) continue;
      visited.add(node);

      ranks.set(node, dist * ranksep);

      for (const succ of adj.succs.get(node) || []) {
        if (!visited.has(succ)) {
          queue.push([succ, dist + 1]);
        }
      }
    }

    for (const node of adj.nodes) {
      if (!ranks.has(node)) {
        ranks.set(node, 0);
      }
    }
  } else {
    for (const node of adj.nodes) {
      ranks.set(node, 0);
    }
  }

  return ranks;
}

function orderNodes(ranks: Map<string, number>, edges: Edge[]): Map<number, string[]> {
  const groups = new Map<number, string[]>();

  for (const [node, rank] of ranks) {
    if (!groups.has(rank)) {
      groups.set(rank, []);
    }
    groups.get(rank)!.push(node);
  }

  const sortedRanks = Array.from(groups.keys()).sort((a, b) => a - b);

  for (let iter = 0; iter < 3; iter++) {
    for (let i = 1; i < sortedRanks.length; i++) {
      const currRank = sortedRanks[i];
      const prevRank = sortedRanks[i - 1];
      barycenterSort(groups.get(currRank)!, edges, groups.get(prevRank)!);
    }

    for (let i = sortedRanks.length - 2; i >= 0; i--) {
      const currRank = sortedRanks[i];
      const nextRank = sortedRanks[i + 1];
      barycenterSort(groups.get(currRank)!, edges, groups.get(nextRank)!);
    }
  }

  return groups;
}

function barycenterSort(group: string[], edges: Edge[], neighborGroup: string[]): void {
  const positions = new Map<string, number>();

  for (const node of group) {
    let totalWeight = 0;
    let weightedSum = 0;

    for (const edge of edges) {
      // Check if edge connects node to neighborGroup
      const other =
        edge.from === node && neighborGroup.includes(edge.to)
          ? edge.to
          : edge.to === node && neighborGroup.includes(edge.from)
            ? edge.from
            : null;

      if (other) {
        const weightStr = String(edge.attributes.weight || '1');
        const weight = Math.max(parseFloat(weightStr) || 1, 0.1);
        const neighborPos = neighborGroup.indexOf(other);
        if (neighborPos >= 0) {
          weightedSum += neighborPos * weight;
          totalWeight += weight;
        }
      }
    }

    if (totalWeight > 0) {
      positions.set(node, weightedSum / totalWeight);
    }
  }

  group.sort((a, b) => {
    const posA = positions.get(a) ?? Infinity;
    const posB = positions.get(b) ?? Infinity;
    return posA - posB;
  });
}

function computeCoordinates(
  rankGroups: Map<number, string[]>,
  nodes: Map<string, NodeAttributes>,
  attrs: Record<string, string>
): LayoutNode[] {
  const layoutNodes: LayoutNode[] = [];
  const nodesep = parseFloat(attrs.nodesep || '18');
  const margin = parseFloat(attrs.margin || '8');
  const rankdir = attrs.rankdir || 'TB';

  const sortedRanks = Array.from(rankGroups.keys()).sort((a, b) => a - b);

  // For LR/RL, we need to know the max width of each rank to calculate X positions
  // For TB/BT, we need to know the max height of each rank to calculate Y positions
  const rankSizes = new Map<number, number>();
  for (const rank of sortedRanks) {
    const group = rankGroups.get(rank)!;
    let maxSize = 0;
    for (const nodeId of group) {
      const nodeAttrs = nodes.get(nodeId) || {};
      const label = String(nodeAttrs.label || nodeId);
      // Estimate size: 0.6 * fontsize * charCount
      const fontsize = parseFloat(String(nodeAttrs.fontsize || '14'));
      const estimatedWidth = Math.max(
        (parseFloat(String(nodeAttrs.width || '')) || 0.75) * 72,
        label.length * fontsize * 0.6
      );
      const estimatedHeight = Math.max(
        (parseFloat(String(nodeAttrs.height || '')) || 0.5) * 72,
        fontsize * 1.5
      );

      if (rankdir === 'LR' || rankdir === 'RL') {
        maxSize = Math.max(maxSize, estimatedWidth);
      } else {
        maxSize = Math.max(maxSize, estimatedHeight);
      }
    }
    rankSizes.set(rank, maxSize);
  }

  const ranksep = parseFloat(attrs.ranksep || '50');
  const rankPositions = new Map<number, number>();
  let currentPos = margin;
  for (const rank of sortedRanks) {
    const size = rankSizes.get(rank) || 0;
    rankPositions.set(rank, currentPos + size / 2);
    currentPos += size + ranksep;
  }

  for (const rank of sortedRanks) {
    const group = rankGroups.get(rank)!;
    const rankPos = rankPositions.get(rank)!;

    let intraRankPos = margin;

    for (const nodeId of group) {
      const nodeAttrs = nodes.get(nodeId) || {};
      const shape = (nodeAttrs.shape || 'round');
      const labelStr = String(nodeAttrs.label || nodeId);
      const fontsize = parseFloat(String(nodeAttrs.fontsize || '14'));
      const width = Math.max(
        (parseFloat(String(nodeAttrs.width || '')) || 0.75) * 72,
        labelStr.length * fontsize * 0.6
      );
      const height = Math.max(
        (parseFloat(String(nodeAttrs.height || '')) || 0.5) * 72,
        fontsize * 1.5
      );

      const labelContent = nodeAttrs.label
        ? typeof nodeAttrs.label === 'string'
          ? parseLabel(nodeAttrs.label)
          : nodeAttrs.label
        : { type: 'string' as const, value: nodeId };

      // Determine coordinates based on rankdir
      let x, y;
      if (rankdir === 'LR' || rankdir === 'RL') {
        // Ranks are horizontal
        x = rankdir === 'LR' ? rankPos : currentPos - rankPos;
        y = intraRankPos + height / 2;
        intraRankPos += height + nodesep;
      } else {
        // Ranks are vertical (TB or BT)
        x = intraRankPos + width / 2;
        y = rankdir === 'TB' ? rankPos : currentPos - rankPos;
        intraRankPos += width + nodesep;
      }

      layoutNodes.push({
        id: nodeId,
        x,
        y,
        width,
        height,
        shape,
        label: labelContent,
        style: {
          fill: nodeAttrs.fillcolor,
          stroke: nodeAttrs.color,
          font: nodeAttrs.fontname || 'NotoSans',
          fontcolor: nodeAttrs.fontcolor,
          fontsize,
        },
      });
    }
  }

  return layoutNodes;
}

function layoutClusters(graph: DotGraph, layoutNodes: LayoutNode[]): LayoutCluster[] {
  const clusters: LayoutCluster[] = [];

  for (const stmt of graph.statements) {
    if (stmt.type === 'subgraph' && stmt.id?.startsWith('cluster_')) {
      const nodeIds = extractClusterNodes(stmt);
      const nodes = layoutNodes.filter((n) => nodeIds.includes(n.id));

      if (nodes.length > 0) {
        const minX = Math.min(...nodes.map((n) => n.x - n.width / 2));
        const maxX = Math.max(...nodes.map((n) => n.x + n.width / 2));
        const minY = Math.min(...nodes.map((n) => n.y - n.height / 2));
        const maxY = Math.max(...nodes.map((n) => n.y + n.height / 2));

        clusters.push({
          id: stmt.id,
          nodeIds,
          rect: { x: minX - 10, y: minY - 10, width: maxX - minX + 20, height: maxY - minY + 20 },
          label:
            typeof stmt.attributes.label === 'string'
              ? stmt.attributes.label
              : stmt.attributes.label?.type === 'string'
                ? stmt.attributes.label.value
                : undefined,
          style: {
            fill: stmt.attributes.style === 'filled' ? stmt.attributes.color : undefined,
            stroke: stmt.attributes.color,
          },
        });
      }
    }
  }

  return clusters;
}

function extractClusterNodes(subgraph: Subgraph): string[] {
  const nodes: string[] = [];

  for (const stmt of subgraph.statements) {
    if (stmt.type === 'node') {
      nodes.push(stmt.nodeId);
    } else if (stmt.type === 'edge') {
      for (const edge of stmt.edges) {
        nodes.push(edge.from, edge.to);
      }
    }
  }

  return [...new Set(nodes)];
}

function routeEdges(
  layoutNodes: LayoutNode[],
  edges: Edge[],
  attrs: Record<string, string>
): LayoutEdge[] {
  const nodeMap = new Map<string, LayoutNode>();
  for (const node of layoutNodes) {
    nodeMap.set(node.id, node);
  }

  const rankDirection = attrs.rankdir || 'TB';

  return edges.map((edge) => {
    const from = nodeMap.get(edge.from);
    const to = nodeMap.get(edge.to);

    if (!from || !to) {
      return {
        from: edge.from,
        to: edge.to,
        points: [
          { x: 0, y: 0 },
          { x: 0, y: 0 },
        ],
        style: {},
      };
    }

    const useOrthogonal = attrs.concentrate !== 'false';
    const points = useOrthogonal
      ? generateOrthogonalEdge(from, to, rankDirection)
      : generateSplinePoints(from, to, rankDirection);

    return {
      from: edge.from,
      to: edge.to,
      points,
      style: {
        stroke: edge.attributes.color || 'black',
        strokeWidth: parseFloat(String(edge.attributes.penwidth || '1')),
        strokeDash:
          edge.attributes.style === 'dashed'
            ? [5, 3]
            : edge.attributes.style === 'dotted'
              ? [2, 2]
              : undefined,
        arrowhead: edge.attributes.arrowhead,
      },
    };
  });
}

function generateSplinePoints(
  from: LayoutNode,
  to: LayoutNode,
  rankDirection = 'TB'
): { x: number; y: number }[] {
  const fromRight = from.x + from.width / 2;
  const fromLeft = from.x - from.width / 2;
  const fromTop = from.y - from.height / 2;
  const fromBottom = from.y + from.height / 2;

  const toRight = to.x + to.width / 2;
  const toLeft = to.x - to.width / 2;
  const toTop = to.y - to.height / 2;
  const toBottom = to.y + to.height / 2;

  let startX: number, startY: number, endX: number, endY: number;

  switch (rankDirection) {
    case 'LR':
      startX = fromRight;
      startY = from.y;
      endX = toLeft;
      endY = to.y;
      break;
    case 'RL':
      startX = fromLeft;
      startY = from.y;
      endX = toRight;
      endY = to.y;
      break;
    case 'BT':
      startX = from.x;
      startY = fromTop;
      endX = to.x;
      endY = toBottom;
      break;
    case 'TB':
    default:
      startX = from.x;
      startY = fromBottom;
      endX = to.x;
      endY = toTop;
      break;
  }

  const dx = Math.abs(endX - startX);
  const dy = Math.abs(endY - startY);

  if (dx < 10 || dy < 10) {
    return [
      { x: startX, y: startY },
      { x: endX, y: endY },
    ];
  }

  const ctrlOffset = Math.min(dx, dy) * 0.5;

  const points: { x: number; y: number }[] = [{ x: startX, y: startY }];

  switch (rankDirection) {
    case 'LR':
      points.push({ x: startX + ctrlOffset, y: startY });
      points.push({ x: endX - ctrlOffset, y: endY });
      break;
    case 'RL':
      points.push({ x: startX - ctrlOffset, y: startY });
      points.push({ x: endX + ctrlOffset, y: endY });
      break;
    case 'BT':
      points.push({ x: startX, y: startY - ctrlOffset });
      points.push({ x: endX, y: endY + ctrlOffset });
      break;
    case 'TB':
    default:
      points.push({ x: startX, y: startY + ctrlOffset });
      points.push({ x: endX, y: endY - ctrlOffset });
      break;
  }

  points.push({ x: endX, y: endY });

  return points;
}

function generateOrthogonalEdge(
  from: LayoutNode,
  to: LayoutNode,
  rankDirection = 'TB'
): { x: number; y: number }[] {
  const points: { x: number; y: number }[] = [];

  const fromRight = from.x + from.width / 2;
  const fromLeft = from.x - from.width / 2;
  const fromTop = from.y - from.height / 2;
  const fromBottom = from.y + from.height / 2;

  const toRight = to.x + to.width / 2;
  const toLeft = to.x - to.width / 2;
  const toTop = to.y - to.height / 2;
  const toBottom = to.y + to.height / 2;

  switch (rankDirection) {
    case 'TB':
      points.push({ x: from.x, y: fromBottom });
      const midY = (fromBottom + toTop) / 2;
      if (from.x !== to.x) {
        points.push({ x: from.x, y: midY });
        points.push({ x: to.x, y: midY });
      }
      points.push({ x: to.x, y: toTop });
      break;
    case 'BT':
      points.push({ x: from.x, y: fromTop });
      const midY2 = (fromTop + toBottom) / 2;
      if (from.x !== to.x) {
        points.push({ x: from.x, y: midY2 });
        points.push({ x: to.x, y: midY2 });
      }
      points.push({ x: to.x, y: toBottom });
      break;
    case 'LR':
      points.push({ x: fromRight, y: from.y });
      const midX1 = (fromRight + toLeft) / 2;
      if (from.y !== to.y) {
        points.push({ x: midX1, y: from.y });
        points.push({ x: midX1, y: to.y });
      }
      points.push({ x: toLeft, y: to.y });
      break;
    case 'RL':
      points.push({ x: fromLeft, y: from.y });
      const midX2 = (fromLeft + toRight) / 2;
      if (from.y !== to.y) {
        points.push({ x: midX2, y: from.y });
        points.push({ x: midX2, y: to.y });
      }
      points.push({ x: toRight, y: to.y });
      break;
  }

  return points;
}

export function computeBoundingBox(
  layoutNodes: LayoutNode[],
  layoutEdges: LayoutEdge[]
): { width: number; height: number; minX: number; minY: number } {
  if (layoutNodes.length === 0) {
    return { width: 100, height: 100, minX: 0, minY: 0 };
  }

  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;

  for (const node of layoutNodes) {
    minX = Math.min(minX, node.x - node.width / 2);
    maxX = Math.max(maxX, node.x + node.width / 2);
    minY = Math.min(minY, node.y - node.height / 2);
    maxY = Math.max(maxY, node.y + node.height / 2);
  }

  // Also check edges for bounding box
  for (const edge of layoutEdges) {
    for (const point of edge.points) {
      minX = Math.min(minX, point.x);
      maxX = Math.max(maxX, point.x);
      minY = Math.min(minY, point.y);
      maxY = Math.max(maxY, point.y);
    }
  }

  return {
    width: maxX - minX + 20, // 10 padding on each side
    height: maxY - minY + 20, // 10 padding on each side
    minX: minX - 10, // Account for left margin
    minY: minY - 10, // Account for bottom margin
  };
}

export function layoutGraphWithSizes(
  graph: DotGraph,
  nodeSizes: Map<string, { width: number; height: number }>
): GraphLayout {
  const nodes = collectNodes(graph);
  const edges = collectEdges(graph);

  const sameRankGroups: string[][] = [];
  function collectNodeIdsFromSubgraph(subgraph: Subgraph): string[] {
    const nodeIds: string[] = [];
    for (const stmt of subgraph.statements) {
      if (stmt.type === 'node') {
        nodeIds.push(stmt.nodeId);
      } else if (stmt.type === 'subgraph') {
        nodeIds.push(...collectNodeIdsFromSubgraph(stmt));
      }
    }
    return nodeIds;
  }

  for (const stmt of graph.statements) {
    if (stmt.type === 'subgraph' && stmt.attributes.rank === 'same') {
      const nodeIds = collectNodeIdsFromSubgraph(stmt);
      if (nodeIds.length > 0) {
        sameRankGroups.push(nodeIds);
      }
    }
  }

  const nodeRankConstraints = new Map<string, 'min' | 'max' | 'source' | 'sink'>();
  for (const [id, attrs] of nodes) {
    if (attrs.rank && attrs.rank !== 'same') {
      nodeRankConstraints.set(id, attrs.rank);
    }
  }

  const filteredEdges = edges.filter((edge) => edge.attributes.constraint !== 'false');

  const adj = buildAdjacency(nodes, filteredEdges);
  const reversedEdges = makeAcyclic(adj);

  const edgeMap = new Map<string, Edge>();
  for (const edge of filteredEdges) {
    edgeMap.set(`${edge.from}|${edge.to}`, edge);
  }
  const acyclicEdges: Edge[] = [];
  for (const u of adj.nodes) {
    for (const v of adj.succs.get(u) || []) {
      const key = `${u}|${v}`;
      const revKey = `${v}|${u}`;
      let edge = edgeMap.get(key);
      if (!edge && reversedEdges.has(revKey)) {
        edge = edgeMap.get(revKey);
        if (edge) {
          acyclicEdges.push({ from: u, to: v, attributes: edge.attributes });
          continue;
        }
      }
      if (edge) {
        acyclicEdges.push(edge);
      }
    }
  }

  const graphAttrs = attrsToRecord(graph.attributes);
  const ranksep = parseFloat(graphAttrs.ranksep || '50');
  const ranks = networkSimplexRankAssignment(
    adj.nodes,
    acyclicEdges,
    adj.preds,
    adj.succs,
    ranksep
  );

  if (nodeRankConstraints.size > 0) {
    const allRanks = Array.from(ranks.values());
    const minRank = Math.min(...allRanks);
    const maxRank = Math.max(...allRanks);

    for (const [nodeId, constraint] of nodeRankConstraints) {
      switch (constraint) {
        case 'min':
          ranks.set(nodeId, minRank);
          break;
        case 'max':
          ranks.set(nodeId, maxRank);
          break;
        case 'source':
          ranks.set(nodeId, minRank);
          break;
        case 'sink':
          ranks.set(nodeId, maxRank);
          break;
      }
    }
  }

  for (const group of sameRankGroups) {
    if (group.length > 0) {
      const total = group.reduce((sum, id) => sum + (ranks.get(id) || 0), 0);
      const avg = total / group.length;
      for (const nodeId of group) {
        ranks.set(nodeId, avg);
      }
    }
  }
  const rankGroups = orderNodes(ranks, filteredEdges);

  const layoutNodes = computeCoordinatesWithSizes(rankGroups, nodes, graphAttrs, nodeSizes);
  const clusters = layoutClusters(graph, layoutNodes);
  const layoutEdges = routeEdges(layoutNodes, edges, graphAttrs);
  const boundingBox = computeBoundingBox(layoutNodes, layoutEdges);

  return {
    nodes: layoutNodes,
    edges: layoutEdges,
    clusters,
    boundingBox,
    rankDirection: graph.attributes.rankdir || 'TB',
  };
}

function computeCoordinatesWithSizes(
  rankGroups: Map<number, string[]>,
  nodes: Map<string, NodeAttributes>,
  attrs: Record<string, string>,
  nodeSizes: Map<string, { width: number; height: number }>
): LayoutNode[] {
  const layoutNodes: LayoutNode[] = [];
  const nodesep = parseFloat(attrs.nodesep || '18');
  const margin = parseFloat(attrs.margin || '8');
  const rankdir = attrs.rankdir || 'TB';

  const sortedRanks = Array.from(rankGroups.keys()).sort((a, b) => a - b);

  const rankSizes = new Map<number, number>();
  for (const rank of sortedRanks) {
    const group = rankGroups.get(rank)!;
    let maxSize = 0;
    for (const nodeId of group) {
      const measured = nodeSizes.get(nodeId);
      if (measured) {
        if (rankdir === 'LR' || rankdir === 'RL') {
          maxSize = Math.max(maxSize, measured.width);
        } else {
          maxSize = Math.max(maxSize, measured.height);
        }
      } else {
        const nodeAttrs = nodes.get(nodeId) || {};
        const label = String(nodeAttrs.label || nodeId);
        const fontsize = parseFloat(String(nodeAttrs.fontsize || '14'));
        const estimatedWidth = Math.max(
          (parseFloat(String(nodeAttrs.width || '')) || 0.75) * 72,
          label.length * fontsize * 0.6
        );
        const estimatedHeight = Math.max(
          (parseFloat(String(nodeAttrs.height || '')) || 0.5) * 72,
          fontsize * 1.5
        );
        if (rankdir === 'LR' || rankdir === 'RL') {
          maxSize = Math.max(maxSize, estimatedWidth);
        } else {
          maxSize = Math.max(maxSize, estimatedHeight);
        }
      }
    }
    rankSizes.set(rank, maxSize);
  }

  const ranksep = parseFloat(attrs.ranksep || '50');
  const rankPositions = new Map<number, number>();
  let currentPos = margin;
  for (const rank of sortedRanks) {
    const size = rankSizes.get(rank) || 0;
    rankPositions.set(rank, currentPos + size / 2);
    currentPos += size + ranksep;
  }

  for (const rank of sortedRanks) {
    const group = rankGroups.get(rank)!;
    const rankPos = rankPositions.get(rank)!;

    let intraRankPos = margin;

    for (const nodeId of group) {
      const nodeAttrs = nodes.get(nodeId) || {};
      const shape = (nodeAttrs.shape || 'round');
      const fontsize = parseFloat(String(nodeAttrs.fontsize || '14'));

      const labelContent = nodeAttrs.label
        ? typeof nodeAttrs.label === 'string'
          ? parseLabel(nodeAttrs.label)
          : nodeAttrs.label
        : { type: 'string' as const, value: nodeId };

      let width: number;
      let height: number;

      const measured = nodeSizes.get(nodeId);
      const hasExplicitSize = nodeAttrs.width || nodeAttrs.height;

      if (measured && !hasExplicitSize) {
        width = Math.max(measured.width + 8, 40);
        height = Math.max(measured.height + 4, 20);
      } else if (hasExplicitSize) {
        width = (parseFloat(String(nodeAttrs.width || '')) || 0.75) * 72;
        height = (parseFloat(String(nodeAttrs.height || '')) || 0.5) * 72;
      } else {
        width = 54;
        height = 18;
      }

      let x, y;
      if (rankdir === 'LR' || rankdir === 'RL') {
        x = rankdir === 'LR' ? rankPos : currentPos - rankPos;
        y = intraRankPos + height / 2;
        intraRankPos += height + nodesep;
      } else {
        x = intraRankPos + width / 2;
        y = rankdir === 'TB' ? rankPos : currentPos - rankPos;
        intraRankPos += width + nodesep;
      }

      layoutNodes.push({
        id: nodeId,
        x,
        y,
        width,
        height,
        shape,
        label: labelContent,
        style: {
          fill: nodeAttrs.fillcolor,
          stroke: nodeAttrs.color,
          font: nodeAttrs.fontname || 'NotoSans',
          fontcolor: nodeAttrs.fontcolor,
          fontsize,
        },
      });
    }
  }

  return layoutNodes;
}
